// pages/Community/SkoolConversations.jsx
//
// The Skool DM threads tab. Outreach opens a conversation; skoolInboxScheduler
// keeps it moving toward one of two conversions the operator defined — the
// contact says they want to START a SaaS business, or that they want HELP
// running their existing business with AI.
//
// Threads live on the same ConnectorConversation model as LinkedIn/Facebook, so
// this reads the existing /conversations endpoints with platform=skool. Nothing
// here is Skool-specific except the filter and the framing.
import React, { useEffect, useState, useCallback } from "react";
import addNotification from "helpers/notify";
import { getConversations, getConversation, sendConversationMessage, getSkoolProspects, setSkoolProspectState } from "api/connectors/connectors";
import { COLUMNS as PROSPECT_STATES } from "./SkoolProspects";

// Prospect state (worker services/prospectClassifier) — the thing that decides
// what every lane may say to this person, so it belongs next to the thread.
const PROSPECT = Object.fromEntries(PROSPECT_STATES.map((c) => [c.key, c]));
const SOURCE_LABEL = { manual: "set by you", booking: "booking", rule: "rule", ai: "AI" };
const handleOf = (profileUrl) => String(String(profileUrl || "").split("/@")[1] || "").toLowerCase();

function ProspectPill({ p }) {
  if (!p) return null;
  const c = PROSPECT[p.state] || PROSPECT.new;
  return (
    <span className="badge" data-testid="prospect-pill" title={`${c.label} (${SOURCE_LABEL[p.stateSource] || p.stateSource}) — ${p.reason || c.hint}`}
      style={{ background: c.bg, color: c.color, border: `1px solid ${c.color}33` }}>
      {c.label}{p.stateSource === "manual" ? " ✎" : ""}
    </span>
  );
}

const STATUS_STYLE = {
  converted: { bg: "#e7f7ec", fg: "#1a7f37", label: "Converted" },
  active:    { bg: "#eef4ff", fg: "#0b3d91", label: "Active" },
  closed:    { bg: "#f2f3f5", fg: "#667085", label: "Closed" },
};
const fmt = (d) => { try { return d ? new Date(d).toLocaleString() : "—"; } catch (_) { return "—"; } };

export default function SkoolConversations({ brandId }) {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState("");
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const [prospects, setProspects] = useState({});   // handle → ProspectState
  const [stateFilter, setStateFilter] = useState("");
  const [savingState, setSavingState] = useState(false);

  const loadProspects = useCallback(async () => {
    try {
      const res = await getSkoolProspects(brandId ? { brandId } : {});
      const map = {};
      ((res && res.prospects) || []).forEach((p) => { if (p.handle) map[String(p.handle).toLowerCase()] = p; });
      setProspects(map);
    } catch (_) { /* the threads still work without states */ }
  }, [brandId]);

  const load = useCallback(async () => {
    setLoading(true);
    loadProspects();
    try {
      const res = await getConversations({ platform: "skool", ...(brandId ? { brandId } : {}) });
      setRows((res && (res.data || res.conversations)) || []);
    } catch (e) {
      addNotification(e?.message || "Could not load Skool conversations", "error");
    } finally { setLoading(false); }
  }, [brandId, loadProspects]);

  const prospectFor = (r) => prospects[handleOf(r && r.contact && r.contact.profileUrl)] || null;

  const changeState = async (p, state) => {
    if (!p || !state) return;
    setSavingState(true);
    try {
      await setSkoolProspectState(p._id, state, state === "auto" ? "" : "set from the DM thread");
      addNotification(state === "auto" ? "Handed back to the classifier" : `Now: ${(PROSPECT[state] || {}).label || state}`, "success");
      await loadProspects();
    } catch (e) {
      addNotification(e?.error || e?.message || "Could not change the state", "error");
    } finally { setSavingState(false); }
  };

  useEffect(() => { load(); }, [load]);

  const open = async (id) => {
    setOpenId(id); setThread(null);
    try {
      const res = await getConversation(id);
      setThread((res && (res.data || res)) || null);
    } catch (e) {
      addNotification(e?.message || "Could not load the thread", "error");
    }
  };

  const send = async () => {
    if (!reply.trim() || !openId) return;
    setSending(true);
    try {
      await sendConversationMessage(openId, { text: reply.trim() });
      setReply("");
      await open(openId);
      addNotification("Reply queued", "success");
    } catch (e) {
      addNotification(e?.message || "Could not send", "error");
    } finally { setSending(false); }
  };

  const counts = rows.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {});
  const shown = stateFilter
    ? rows.filter((r) => ((prospectFor(r) || {}).state || "unclassified") === stateFilter)
    : rows;

  return (
    <div>
      <div className="d-flex align-items-center mb-2" style={{ gap: 10 }}>
        <h6 className="mb-0">Skool DM threads</h6>
        <span className="text-muted small">
          {rows.length} total · {counts.converted || 0} converted · {counts.active || 0} active
        </span>
        <select className="form-control form-control-sm ml-auto" style={{ width: 200 }} aria-label="Prospect state filter"
          value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
          <option value="">All prospect states</option>
          {PROSPECT_STATES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          <option value="unclassified">Not classified yet</option>
        </select>
        <button className="btn btn-link btn-sm p-0" onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>
      <p className="text-muted small mb-3">
        A thread counts as <strong>converted</strong> only when the contact says they want to start a
        SaaS business, or that they want help running their business with AI. The follow-up stops
        automatically at that point, and after 3 AI replies without a human it waits for you.
      </p>

      {!rows.length && !loading && (
        <div className="text-muted small">
          {/* The old wording said threads appear "once someone replies", which is
              wrong — a thread is recorded as soon as a DM is SENT, reply or not.
              Combined with this tab defaulting to the wrong brand, it read as
              "nobody has answered" when the real answer was "you are looking at
              a brand with no Skool community". Name the brand, so an empty list
              is never ambiguous again. */}
          No Skool threads for this brand.
          {brandId ? " Threads are recorded as soon as a DM goes out — reply or not — so check the brand selected above." : " Pick a brand above."}
        </div>
      )}

      <div className="row">
        <div className={openId ? "col-md-5" : "col-md-12"}>
          {shown.map((r) => {
            const st = STATUS_STYLE[r.status] || STATUS_STYLE.closed;
            return (
              <button
                key={r._id}
                onClick={() => open(r._id)}
                className="btn btn-light btn-block text-left mb-2"
                style={{ border: openId === r._id ? "2px solid #0b3d91" : "1px solid #e4e7ec" }}
              >
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <strong className="small">{r.contact?.name || "(unknown)"}</strong>
                  <span className="badge" style={{ background: st.bg, color: st.fg }}>{st.label}</span>
                  <ProspectPill p={prospectFor(r)} />
                  <span className="text-muted small ml-auto">{fmt(r.lastMessageAt || r.updatedAt)}</span>
                </div>
                {r.contact?.profileUrl && (
                  <div className="text-muted" style={{ fontSize: "0.72rem" }}>{r.contact.profileUrl}</div>
                )}
              </button>
            );
          })}
        </div>

        {openId && (
          <div className="col-md-7">
            <div className="card">
              <div className="card-body">
                {!thread && <div className="text-muted small">Loading thread…</div>}
                {thread && (
                  <>
                    <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
                      <strong>{thread.contact?.name}</strong>
                      <span className="badge" style={{
                        background: (STATUS_STYLE[thread.status] || STATUS_STYLE.closed).bg,
                        color: (STATUS_STYLE[thread.status] || STATUS_STYLE.closed).fg,
                      }}>{(STATUS_STYLE[thread.status] || STATUS_STYLE.closed).label}</span>
                      <ProspectPill p={prospectFor(thread)} />
                      <button className="btn btn-link btn-sm ml-auto p-0" onClick={() => setOpenId("")}>close</button>
                    </div>
                    {(() => {
                      const p = prospectFor(thread);
                      if (!p) return <div className="text-muted small mb-2">Not classified yet — the classifier runs hourly.</div>;
                      const c = PROSPECT[p.state] || PROSPECT.new;
                      return (
                        <div className="mb-2 p-2" style={{ background: c.bg, borderRadius: 8, fontSize: "0.78rem" }}>
                          <div className="d-flex align-items-center" style={{ gap: 8 }}>
                            <b style={{ color: c.color }}>{c.label}</b>
                            <span className="text-muted">· {SOURCE_LABEL[p.stateSource] || p.stateSource}</span>
                            <select className="form-control form-control-sm ml-auto" style={{ width: 190 }} aria-label="Change prospect state"
                              value="" disabled={savingState} onChange={(e) => changeState(p, e.target.value)}>
                              <option value="">Change state…</option>
                              {PROSPECT_STATES.filter((x) => x.key !== p.state || p.stateSource !== "manual").map((x) => (
                                <option key={x.key} value={x.key}>{x.label}</option>
                              ))}
                              {p.stateSource === "manual" && <option value="auto">Back to automatic</option>}
                            </select>
                          </div>
                          {p.reason && <div style={{ color: "#475467", marginTop: 4 }}>{p.reason}</div>}
                          {p.booking && p.booking.startsAt && (
                            <div style={{ color: "#6a3fb5", marginTop: 4 }}>Call booked: {fmt(p.booking.startsAt)}</div>
                          )}
                          <div className="text-muted" style={{ marginTop: 4 }}>{c.hint}</div>
                        </div>
                      );
                    })()}

                    <div style={{ maxHeight: 380, overflowY: "auto", padding: "4px 0" }}>
                      {(thread.thread || []).map((m, i) => (m.status === "failed" && m.direction === "out") ? (
                        <div key={i} data-testid="not-sent" className="mb-2 d-flex" style={{ justifyContent: "flex-end" }}>
                          <div title={m.text} style={{ maxWidth: "82%", padding: "4px 10px", borderRadius: 10, fontSize: "0.72rem", color: "#98a2b3", border: "1px dashed #d0d5dd" }}>
                            Not sent{m.error ? ` — ${m.error}` : ""} · {String(m.text || "").slice(0, 60)}…
                          </div>
                        </div>
                      ) : (
                        <div key={i} className="mb-2 d-flex" style={{ justifyContent: m.direction === "out" ? "flex-end" : "flex-start" }}>
                          <div style={{
                            maxWidth: "82%", padding: "8px 10px", borderRadius: 10, whiteSpace: "pre-wrap",
                            fontSize: "0.84rem",
                            background: m.direction === "out" ? "#0b3d91" : "#f2f3f5",
                            color: m.direction === "out" ? "#fff" : "#101828",
                          }}>
                            {m.text}
                          </div>
                        </div>
                      ))}
                      {!(thread.thread || []).length && <div className="text-muted small">No messages recorded yet.</div>}
                    </div>

                    <hr />
                    <textarea
                      rows={3} className="form-control form-control-sm mb-2"
                      placeholder="Write a reply — this takes over from the AI for this thread"
                      value={reply} onChange={(e) => setReply(e.target.value)}
                    />
                    <button className="btn btn-primary btn-sm" onClick={send} disabled={sending || !reply.trim()}>
                      {sending ? "Sending…" : "Send reply"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
